/**
 * Avisa cuando un elemento se acerca a la pantalla (IntersectionObserver).
 *
 * Lo usa el scroll infinito: un marcador al final de la lista pide la pagina
 * siguiente antes de que el comprador llegue al fondo, con `MARGEN` de
 * anticipacion para que no alcance a ver el hueco.
 *
 * El observador se rearma cada vez que cambian las `claves` (p. ej. cuantos
 * productos hay cargados). No es un detalle: si la pagina nueva fue corta y el
 * marcador SIGUE a la vista, el observador viejo no vuelve a avisar —solo avisa
 * cuando algo cambia de estar fuera a estar dentro—, y la carga se quedaria
 * trabada. Uno recien armado avisa de inmediato si el marcador ya esta visible.
 */
import { useEffect, useRef, useState } from 'react';

/** Cuanto antes del fondo se avisa: un par de filas de tarjetas. */
const MARGEN = '600px 0px';

export function useAlVer(
  alVer: () => void,
  claves: ReadonlyArray<string | number | boolean>,
): (elemento: HTMLElement | null) => void {
  const [elemento, setElemento] = useState<HTMLElement | null>(null);
  // En una ref para que cambiar la funcion no rearme el observador.
  const aviso = useRef(alVer);
  aviso.current = alVer;

  useEffect(() => {
    if (elemento === null) return;

    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((entrada) => entrada.isIntersecting)) aviso.current();
      },
      { rootMargin: MARGEN },
    );
    observador.observe(elemento);

    return () => {
      observador.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elemento, ...claves]);

  return setElemento;
}
